import { useEffect, useState } from "react";
import { apiUrl } from "utils/apiUrl";
import { useLocation, useNavigate } from "react-router-dom";
import { readAdminSession, saveAdminSession } from "utils/adminSession";
import { clearClientSession } from "utils/clientSession";

const INPUT_CLASS =
  "mt-2 mb-4 flex h-11 w-full rounded-lg border border-white/15 bg-white/5 px-3 text-sm text-white outline-none placeholder:text-gray-500 focus:border-brand-400";

function parseJson(text) {
  const t = (text || "").trim();
  if (!t) return null;
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

function adminLandingPath(location) {
  const from = location.state?.from;
  if (typeof from === "string" && from.startsWith("/admin")) {
    return from;
  }
  return "/admin/default";
}

export default function AdminOwnerLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorText, setErrorText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (readAdminSession()) {
      navigate(adminLandingPath(location), { replace: true });
    }
  }, [location, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorText("");
    const emailTrim = email.trim();
    if (!emailTrim || !password) {
      setErrorText("Email and password required.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(apiUrl("/api/admin/login"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailTrim, password }),
      });
      const raw = await res.text();
      const data = parseJson(raw);

      if (data == null) {
        setErrorText("Server error. Run npm run dev from the project folder.");
        return;
      }

      if (res.ok && data.ok && data.isAdmin) {
        clearClientSession();
        saveAdminSession({
          clientId: data.clientId,
          isAdmin: true,
          at: Date.now(),
          source: "env",
        });
        navigate(adminLandingPath(location), { replace: true });
        return;
      }

      if (data.code === "ADMIN_PASSWORD_MISMATCH") {
        setErrorText("Wrong password.");
        return;
      }
      if (data.code === "ADMIN_EMAIL_NOT_ALLOWED") {
        setErrorText("Email not allowed.");
        return;
      }
      if (res.status === 404 && data.code === "ADMIN_ENV_NOT_CONFIGURED") {
        setErrorText("Admin not configured in .env.");
        return;
      }
      setErrorText(data.message || "Login failed.");
    } catch {
      setErrorText("Network error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b1437] px-4">
      <div className="w-full max-w-[380px] rounded-xl border border-white/10 bg-[#111c44] p-8 shadow-lg">
        <h1 className="text-center text-2xl font-semibold tracking-tight text-white">
          Admin login
        </h1>
        <p className="mt-2 text-center text-sm text-gray-400">
          Team access only — use your admin e-mail from{" "}
          <code className="text-gray-300">ADMIN_EMAILS</code>.
        </p>

        <form className="mt-8" onSubmit={handleSubmit} noValidate>
          <label className="block text-xs font-medium uppercase tracking-wide text-gray-400">
            Email
            <input
              name="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={INPUT_CLASS}
            />
          </label>
          <label className="block text-xs font-medium uppercase tracking-wide text-gray-400">
            Password
            <input
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={INPUT_CLASS}
            />
          </label>

          {errorText ? (
            <p className="mb-3 text-sm text-red-400">{errorText}</p>
          ) : null}

          <button
            type="submit"
            disabled={loading}
            className="mt-2 w-full rounded-lg bg-brand-500 py-2.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-50"
          >
            {loading ? "…" : "Sign in"}
          </button>
        </form>

      </div>
    </div>
  );
}
